import { useEffect, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, Check, Loader2, Sparkles, X } from 'lucide-react';
import type {
  PersonalSkillDraft,
  SkillChangeRequest,
  SkillChangeResponse,
  SkillChangeResult,
} from '@hector/shared-types';
import { SkillEditorFields, type SkillFieldErrors } from './SkillEditorFields';
import { validateSkillDraft } from './skillUtils';

interface SkillChangeCardProps {
  request: SkillChangeRequest;
  result?: SkillChangeResult;
  onRespond?: (response: Omit<SkillChangeResponse, 'type'>) => boolean;
}

const OPERATION_LABELS = {
  create: { title: 'Enregistrer cette méthode comme compétence ?', submit: 'Créer', done: 'Compétence créée' },
  update: { title: 'Modifier cette compétence ?', submit: 'Sauvegarder', done: 'Compétence mise à jour' },
  delete: { title: 'Supprimer cette compétence ?', submit: 'Supprimer', done: 'Compétence supprimée' },
} as const;

export function SkillChangeCard({ request, result, onRespond }: SkillChangeCardProps) {
  const [draft, setDraft] = useState<PersonalSkillDraft>(() => (
    request.operation === 'delete'
      ? { slug: request.skill.slug, description: '', content: '' }
      : request.skill
  ));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<SkillFieldErrors>({});
  const [sendError, setSendError] = useState<string | null>(null);
  const previousRequest = useRef(request);
  const labels = OPERATION_LABELS[request.operation];

  useEffect(() => {
    if (result) setIsSubmitting(false);
  }, [result]);

  useEffect(() => {
    if (previousRequest.current === request) return;
    previousRequest.current = request;
    if (result) return;

    // A reconnect replay keeps the same requestId and timeline item, but brings
    // a fresh request object. Re-arm only the local consent UI: the backend is
    // still waiting and no second mutation has occurred yet.
    setIsSubmitting(false);
    setSendError(null);
  }, [request, result]);

  const respond = (accepted: boolean) => {
    if (!onRespond || isSubmitting || result) return;
    if (accepted && request.operation !== 'delete') {
      const nextErrors = validateSkillDraft(draft);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;
    }
    setSendError(null);
    setIsSubmitting(true);
    const sent = onRespond({
      requestId: request.requestId,
      accepted,
      ...(accepted && request.operation !== 'delete' ? { skill: draft } : {}),
    });
    if (!sent) {
      setIsSubmitting(false);
      setSendError('Connexion perdue : votre choix n’a pas été envoyé. Réessayez dans un instant.');
    }
  };

  if (result?.status === 'success') {
    return <CardStatus icon={<Check className="h-4 w-4" />} label={`${labels.done}${result.skill ? ` /${result.skill.slug}` : ''}.`} />;
  }
  if (result?.status === 'declined') {
    return <CardStatus icon={<X className="h-4 w-4" />} label="Proposition ignorée. Aucune compétence n’a été modifiée." />;
  }
  if (result?.status === 'expired') {
    return <CardStatus warning label="Proposition expirée : redemandez à Hector si vous souhaitez la reprendre." />;
  }
  if (result?.status === 'error') {
    return <CardStatus warning label={result.error ?? 'La compétence n’a pas pu être modifiée.'} />;
  }

  return (
    <div className="overflow-hidden rounded-lg border border-grey-200 bg-white">
      <div className="flex items-center gap-2 border-b border-grey-100 bg-grey-50/60 px-3.5 py-2">
        <Sparkles className="h-3.5 w-3.5 text-grey-400" />
        <span className="text-xs font-medium text-grey-600">Compétence</span>
        {isSubmitting ? (
          <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-grey-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Validation en cours…
          </span>
        ) : null}
      </div>
      <div className="space-y-3 px-3.5 py-3">
        <div>
          <p className="text-sm font-medium text-grey-900">{labels.title}</p>
          {request.operation === 'delete' ? (
            <p className="mt-1 text-sm text-grey-600">
              La commande /{request.skill.slug} ne sera plus disponible.
            </p>
          ) : (
            <p className="mt-1 text-sm leading-relaxed text-grey-600">
              Une compétence est un prompt décrivant une méthode de travail que l&apos;assistant peut réutiliser à chaque conversation. Vous pouvez la déclencher avec /nom, ou Hector peut la charger automatiquement lorsque sa description correspond.
            </p>
          )}
        </div>
        {request.operation !== 'delete' ? (
          <SkillEditorFields
            value={draft}
            onChange={setDraft}
            errors={errors}
            disabled={isSubmitting}
          />
        ) : null}
      </div>
      {sendError ? (
        <div className="border-t border-amber-200 bg-amber-50 px-3.5 py-2 text-xs text-amber-800">{sendError}</div>
      ) : null}
      {onRespond ? (
        <div className="flex justify-end gap-2 border-t border-grey-100 px-3.5 py-2">
          <button
            type="button"
            onClick={() => respond(false)}
            disabled={isSubmitting}
            className="rounded-lg border border-grey-200 px-3 py-2 text-sm font-medium text-grey-600 transition hover:bg-grey-50 disabled:opacity-50"
          >
            Ignorer
          </button>
          <button
            type="button"
            onClick={() => respond(true)}
            disabled={isSubmitting}
            className="rounded-lg bg-grey-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-grey-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {labels.submit}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function CardStatus({ icon, label, warning = false }: { icon?: ReactNode; label: string; warning?: boolean }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-grey-200 bg-white px-3.5 py-2 text-xs text-grey-600">
      {warning ? <AlertTriangle className="mt-px h-4 w-4 shrink-0 text-amber-500" /> : icon}
      <span>{label}</span>
    </div>
  );
}
